export interface PexelsImage {
  id: number;
  width: number;
  height: number;
  url: string;
  photographer: string;
  src: {
    original: string;
    large2x: string;
    large: string;
    medium: string;
    small: string;
    portrait: string;
    landscape: string;
    tiny: string;
  };
  alt: string;
}

export interface PexelsSearchResponse {
  page: number;
  per_page: number;
  photos: PexelsImage[];
  total_results: number;
  next_page?: string;
}

export class PexelsImageDownloadError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "PexelsImageDownloadError";
  }
}

export class PexelsService {
  private static baseUrl = process.env.REACT_APP_API_BASE;

  static async searchImages(
    query: string,
    idToken: string,
    options?: {
      page?: number;
      per_page?: number;
      orientation?: "landscape" | "portrait" | "square";
    }
  ): Promise<PexelsSearchResponse> {
    const params = new URLSearchParams({
      query,
      page: (options?.page || 1).toString(),
      per_page: (options?.per_page || 20).toString(),
    });

    if (options?.orientation) {
      params.append("orientation", options.orientation);
    }

    const url = `${this.baseUrl}/api/pexels/search-images?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(error.message || "Failed to search images");
    }

    return response.json();
  }

  static async downloadImage(
    url: string,
    idToken: string,
    imageId: number
  ): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/api/pexels/download-image`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ url, imageId }),
    });

    if (!response.ok) {
      const payload = await response
        .json()
        .catch(() => ({ error: "Failed to download image" }));
      throw new PexelsImageDownloadError(
        payload.error || "Failed to download image",
        payload.code || "proxy_failed",
        response.status
      );
    }

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.toLowerCase().startsWith("image/")) {
      throw new PexelsImageDownloadError(
        "The image service returned an unsupported response.",
        "invalid_content_type",
        response.status
      );
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      throw new PexelsImageDownloadError(
        "The image service returned an empty image.",
        "empty_image",
        response.status
      );
    }
    return blob;
  }
}
